import type {Edge, Node, NodeHandle} from "@xyflow/react";
import {Position} from "@xyflow/react";
import {hierarchy, tree} from "d3-hierarchy";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import type {TaxonomySpend} from "~/prototypes/pages/taxonomy-canvas/mockTaxonomySpend.ts";
import {countDescendants} from "~/prototypes/pages/taxonomy-canvas/taxonomyEdits.ts";

export const NODE_WIDTH = 240;
export const NODE_HEIGHT = 56;
const ROW_GAP = 20;
const COLUMN_GAP = 100;

export type TaxonomyCanvasNodeData = {
    label: string
    level: number
    /** null for the root */
    parentId: number | null
    childCount: number
    /** All categories below this one, any depth */
    descendantCount: number
    expanded: boolean
    spend: number
    /** 0..1: spend relative to the highest spend on the same level */
    spendShare: number
    /** Chosen in the level filters */
    focused?: boolean
    /** Rename after confirmation (local only) */
    onRename?: (label: string) => void
    /** Ask to delete this category (local only) */
    onDelete?: () => void
    /** Set on the node under the pointer while another node is dragged over it */
    dropAction?: 'merge' | 'move'
    /** True for the node being dragged */
    dragging?: boolean
}
export type TaxonomyCanvasNode = Node<TaxonomyCanvasNodeData, 'taxonomy'>

/**
 * Edge anchors, declared up front: incoming edges at the left middle, outgoing at the right middle.
 * Without this React Flow measures the handles in the DOM and forgets them whenever a node object is replaced
 * (e.g. the drop-target highlight while dragging), which made those cards' edges disappear.
 */
const HANDLES: NodeHandle[] = [
    {type: 'target', position: Position.Left, x: 0, y: NODE_HEIGHT / 2},
    {type: 'source', position: Position.Right, x: NODE_WIDTH, y: NODE_HEIGHT / 2},
];

const visibleChildren = (n: m_taxonomy.Tree) => n.children.filter(c => !c.removed);

/**
 * Lays out the taxonomy left-to-right; only children of expanded nodes are shown.
 */
export function layoutTaxonomy(
    root: m_taxonomy.Tree,
    rootLabel: string,
    expanded: ReadonlySet<number>,
    spend: TaxonomySpend,
): {nodes: TaxonomyCanvasNode[], edges: Edge[]} {
    const {spendById, maxByDepth} = spend;
    // d3 lays out top-down; swap axes to get a left-to-right tree
    const h = tree<m_taxonomy.Tree>().nodeSize([NODE_HEIGHT + ROW_GAP, NODE_WIDTH + COLUMN_GAP])(
        hierarchy(root, n => expanded.has(n.id) ? visibleChildren(n) : [])
    );

    const nodes: TaxonomyCanvasNode[] = h.descendants().map(d => ({
        id: String(d.data.id),
        type: 'taxonomy',
        position: {x: d.y, y: d.x},
        // The root is the taxonomy itself: it can't be merged or moved
        draggable: d.depth > 0,
        // Fixed size, so fitView and the minimap work without measuring (nodes are not stateful)
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        handles: HANDLES,
        data: {
            label: d.depth === 0 ? rootLabel : d.data.label,
            level: d.depth,
            parentId: d.parent?.data.id ?? null,
            childCount: visibleChildren(d.data).length,
            descendantCount: countDescendants(d.data),
            expanded: expanded.has(d.data.id),
            spend: spendById.get(d.data.id) ?? 0,
            spendShare: maxByDepth[d.depth] ? (spendById.get(d.data.id) ?? 0) / maxByDepth[d.depth] : 0,
        },
    }));
    const edges: Edge[] = h.links().map(l => ({
        id: `${l.source.data.id}-${l.target.data.id}`,
        source: String(l.source.data.id),
        target: String(l.target.data.id),
        type: 'smoothstep',
    }));
    return {nodes, edges};
}
